import React, { ReactElement, CSSProperties, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import Button from '../../../atoms/buttons/Button';
import { FormWrapper, Heading, Description, ButtonAction, StyledContainer } from './styledElements';
import { handlePhoneSettingsVysionEvent } from '../../../utils/vysion/phoneSettings';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { PhoneSettingsViewVysionEventsEnum } from '../../../utils/vysion/phoneSettings/view';
import { DashlineSeparator, HCaptchaContainer } from '../../common/CommonStyle';
import { RootState } from '../../../store';
import HCaptcha from '../../auth/Hcaptcha';
import useVerifyEmailWallModal from 'hooks/useVerifyEmailWallModal';
import { useApp } from 'contexts/AppContext';
import useModal from 'hooks/useModal';
import colors from 'theme/colors';
import { FONT_FAMILIES } from 'theme/material/constants';
import { styled } from '@material-ui/core';
import IconExclamationPointCircle from 'atoms/icons/IconExclamationPointCircle';
import { unlinkPhoneNumber } from 'sdk/ProfileSDK';
import CheckCircleIcon from 'atoms/icons/CheckCircleIcon';
import { refreshProfile } from 'actions/profileActions';
import { storeErrors } from 'actions/commonActions';
import { isQAautomation } from 'utils/helpers';
import { getPatternModal } from 'style';

const ButtonContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 30,
});

const ErrorMessage = styled('div')({
  color: colors.red['1200'],
  fontWeight: 600,
  fontSize: 15,
  marginTop: 20,
  lineHeight: '20px',
  letterSpacing: '-0.3px',
});

interface IVerifyUnlinkPhoneOwnerShipProps {
  customStyle?: CSSProperties;
  onCancel?: () => void;
}

const VerifyUnlinkPhoneOwnerShip = ({ customStyle, onCancel }: IVerifyUnlinkPhoneOwnerShipProps): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const { is_member_hcaptcha_enabled, cf_country } = useSelector((state: RootState) => state.settings);
  const account = useSelector((state: RootState) => state.profile);

  const { showMandatoryVerifyEmailWall, isVerifyEmailRequired } = useVerifyEmailWallModal();
  const { setForceCloseSliderMenu } = useApp();
  const { basicModal, resetModal } = useModal();
  const dispatch = useDispatch();

  const verifyEmailCallback = (): void => {
    setIsProcessing(false);
    setForceCloseSliderMenu(prev => prev + 1);

    if (onCancel) {
      onCancel();
    }
  };

  const isButtonDisabled = (): boolean => {
    if (isQAautomation()) {
      return false;
    }

    return isProcessing || (is_member_hcaptcha_enabled && isEmpty(captcha));
  };

  const confirmUnlinkPhone = (): void => {
    if (isVerifyEmailRequired(account)) {
      showProcessModal(
        handleVerifyEmail,
        'verify_email',
        'Remove Phone Number?',
        'You will not be able to log in using your phone number anymore.',
        'You must have a verified email to continue.'
      );

      return;
    }

    showProcessModal(
      handleUnlinkPhone,
      'confirm',
      'Remove Phone Number?',
      'You will not be able to log in using your phone number anymore.'
    );
  };

  const showProcessModal = (
    callback: () => void,
    action: string,
    title: string,
    description: string,
    error?: string
  ): void => {
    resetModal();
    basicModal({
      dateTestId: 'phone-confirm-unlink',
      modalContentLabel: 'Remove Phone Number?',
      isModalOpen: true,
      withoutBgPattern: true,
      closeOnOverlayClick: false,
      //   onCloseClickHandler: handleVysionClickClose,
      modalBody: (
        <StyledContainer style={{ textAlign: 'left' }}>
          {action === 'success' ? (
            <CheckCircleIcon />
          ) : (
            <IconExclamationPointCircle
              customStyle={{
                width: 40,
                height: 40,
              }}
            />
          )}
          <Heading style={{ marginBottom: 5, marginTop: 5 }}>{title}</Heading>
          <Description>{description}</Description>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <ButtonContainer>
            <Button
              data-test-id="confirm-unlink-phone-button"
              customStyle={{
                fontFamily: FONT_FAMILIES.primary,
                fontSize: 14,
                fontWeight: 600,
                margin: 0,
                lineHeight: '16px',
                padding: '12px 22px',
                borderRadius: '4px !important',
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                '&:disabled': {
                  background: `${colors.gray['200']} !important`,
                  border: 'none',
                  color: '#fff !important',
                },
              }}
              buttonType="primary"
              onClick={callback}
              type={'button'}
              size="small"
            >
              {action === 'verify_email' ? 'Update Email' : 'OK'}
            </Button>
            {action !== 'success' && (
              <Button
                data-test-id="cancel-unlink-phone-button"
                customStyle={{
                  color: getPatternModal().linkGrey,
                  fontSize: 15,
                  lineHeight: '17px',
                  fontWeight: 'normal',
                  height: 'auto',
                  padding: '10px 0',
                }}
                onClick={() => {
                  resetModal();
                  setIsProcessing(false);
                }}
                buttonType="chromeless"
              >
                {error ? 'Close' : 'Nevermind'}
              </Button>
            )}
          </ButtonContainer>
        </StyledContainer>
      ),
      customContentStyle: { maxWidth: 420 },
    });
  };

  const handleVerifyEmail = (): void => {
    setIsProcessing(true);
    showMandatoryVerifyEmailWall(
      () => {
        verifyEmailCallback();
      },
      () => {
        verifyEmailCallback();
      }
    );
  };

  const handleUnlinkPhone = async (): Promise<void> => {
    setIsProcessing(true);

    try {
      await unlinkPhoneNumber({ 'h-captcha-response': captcha });

      dispatch(refreshProfile());
      setIsProcessing(false);
      setForceCloseSliderMenu(prev => prev + 1);
      showProcessModal(
        () => {
          resetModal();
        },
        'success',
        'Success!',
        'Your phone number has been successfully unlinked from your account.'
      );
    } catch (error) {
      setIsProcessing(false);
      resetModal();
      console.error('Error unlinking phone number:', error);
      dispatch(storeErrors(error.response.data));
    }
  };

  useEffect(() => {
    handlePhoneSettingsVysionEvent({
      action: VYSION_ACTIONS.VIEW,
      event: PhoneSettingsViewVysionEventsEnum.VIEW_ACCOUNT_MENU_VERIFY_ACCOUNT,
    });
  }, []);

  return (
    <FormWrapper style={customStyle}>
      <Heading>Please confirm you are human</Heading>
      <Description>Continuing will remove the phone number from your account.</Description>
      {is_member_hcaptcha_enabled && (
        <>
          <DashlineSeparator />
          <HCaptchaContainer style={{ marginTop: 20 }}>
            <HCaptcha cfCountry={cf_country} storeCaptchaResponse={token => setCaptcha(token)} />
          </HCaptchaContainer>
        </>
      )}
      <DashlineSeparator style={{ margin: '20px 0 0' }} />
      <ButtonAction>
        <Button
          data-test-id="phone-settings-button"
          buttonType="primaryRed"
          onClick={confirmUnlinkPhone}
          disabled={isButtonDisabled()}
          type={'button'}
          size="small"
        >
          {isProcessing ? 'Removing Phone Number...' : 'Remove Phone Number'}
        </Button>
      </ButtonAction>
    </FormWrapper>
  );
};

export default VerifyUnlinkPhoneOwnerShip;
